import React, {ChangeEvent, useCallback, useRef} from 'react';
import cx from 'classnames';
import {
    defaultDropDownV2Context,
    DropdownItem,
    DropdownV2,
    DropDownV2Context,
    isSelected,
    Scrollbar,
    Text
} from '@priority-software/priority-style-react';
import {SearchAction} from 'priority-web-sdk/enums';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {AutomationChoose} from '../../../../reducer/model';
import {isRTL} from '../../../../../../Localization';
import {texts} from '../../../../texts';
import {useSearch as useOldSearch} from '../../../../../../components/Common/FieldSearch/hooks/useSearch';
import {keys} from '../../../../../../constants/KeyCodes';
import styles from './styles.module.scss';
import {
    bpmSearchNextPage,
    fetchAdditionalChooseOld,
    fetchAdditionalSearchOld
} from '../../../../modules/BPMRules/api/bpmChooseSearch';
import {SearchContentItem} from './parts/SearchControlParts/SearchContentItem/SearchContentItem';
import {SearchHeader} from './parts/SearchControlParts/SearchHeader';
import {getLocalizedText, texts as commonTexts} from '../../../../../../constants/TextConst';

type Props = {
    isOpen: boolean;
    contentComponentClasses?: string;
    onOpen: () => void;
    onClose: () => void;
    onSelect: (option: AutomationChoose) => void;
    searchPlaceholder?: string;
    session: string;
    value: string;
    relatedField: number;
    trigger: React.ReactElement;
    openingSide?: 'leftSide' | 'rightSide';
    onCriticalError: () => void;
    portalSelector: string;
    requestDataHandler?: any;
};

const eventTypes = ['touchstart', 'mousedown'];

function prepareDataItem(item, searchByTitle = false) {
    return {
        value: item.retval as string,
        label: item.string1 as string,
        description: item.string2 as string,
        data: {
            description2: item.string3 as string
        }
    };
}

export const AutomationBPMFieldSearch = (props: Props) => {
    const {
        contentComponentClasses,
        trigger,
        relatedField,
        value = '',
        isOpen,
        onOpen,
        openingSide,
        onClose,
        onSelect,
        portalSelector
    } = props;
    const inputRef = useRef<HTMLInputElement>();

    const actions = {
        onSearchOpen: async (_, searchQuery) => {
            return await fetchAdditionalChooseOld(relatedField.toString(), searchQuery);
        },
        onSearchAction: async (mode: SearchAction, searchQuery) => {
            return await fetchAdditionalSearchOld(
                relatedField.toString(),
                mode?.toString(),
                searchQuery
            );
        },
        onScroll: async (nextPage: number, searchQuery: string) => {
            return await bpmSearchNextPage(
                relatedField.toString(),
                nextPage.toString(),
                searchQuery
            );
        }
    };
    const [state, handlers] = useOldSearch(actions, value);

    const {
        results: options,
        searchQuery,
        ignorecase,
        searchFromBeginning,
        searchByTitle,
        focusedIndex,
        isSearchable
    } = state;

    const {
        handleSearchOpen,
        handleIgnoreCaseChange,
        handleSearchFromBeginningChange,
        handleNavigationKeys,
        handleScroll,
        handleSearchByChange,
        handleSearchChange,
        handleSearchQueryChange,
        dispatchState
    } = handlers;

    const openDropdown = useCallback(async () => {
        await handleSearchOpen(value);
        onOpen();
    }, [onOpen, value, handleSearchOpen]);

    const toggleOpen = useCallback(async () => {
        if (isOpen) {
            onClose();
        } else {
            await openDropdown();
        }
    }, [isOpen, onClose, openDropdown]);

    const contentRenderer = useCallback(
        (data: DropdownItemData) => {
            const searchAll = isSearchable && !searchByTitle;

            return (
                <SearchContentItem
                    searchQuery={searchQuery}
                    searchByCategory={!!searchByTitle}
                    searchFromBeginning={searchFromBeginning}
                    itemData={data}
                    ignorecase={ignorecase}
                    searchAll={searchAll}
                />
            );
        },
        [searchQuery, searchFromBeginning, ignorecase, isSearchable, searchByTitle]
    );

    const handleSearch = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const searchString = e.target.value;

            if (isSearchable) {
                handleSearchQueryChange(searchString);
                handleSearchChange(searchString);
            } else {
                handleSearchQueryChange(searchString);
                dispatchState(state => ({
                    ...state,
                    results: searchString
                        ? state.results.filter(item =>
                              item.string1.toLowerCase().includes(searchString.toLowerCase())
                          )
                        : state._results
                }));
            }
        },
        [isSearchable, dispatchState]
    );

    const hasClientSearch = state._results.length > 6;
    const hasSearch = !isSearchable ? hasClientSearch : true;

    const handleDropdownKeyDown = event => {
        if (event.key === keys.ENTER) {
            const {focusedIndex, results} = state;
            if (focusedIndex > -1 && event.key === keys.ENTER) {
                event.preventDefault();
                const item = results[focusedIndex];
                if (item) {
                    onSelect(prepareDataItem(item));
                }
            }
        }
        handleNavigationKeys(event);
    };

    return (
        <DropDownV2Context.Provider value={{...defaultDropDownV2Context, isOpen, toggleOpen}}>
            <DropdownV2
                usePortal
                onClose={onClose}
                isOpen={isOpen}
                onOpen={openDropdown}
                onKeyDown={handleDropdownKeyDown}
                contentComponentClasses={cx(contentComponentClasses, styles.dropdownMenu, {
                    [styles.hasExtraPadding]: hasSearch
                })}
                portalSelector={portalSelector}
                trigger={trigger}
                openingSide={openingSide}
                eventTypes={eventTypes}
            >
                <SearchHeader
                    hasSearch={hasSearch}
                    inputRef={inputRef}
                    searchQuery={searchQuery}
                    ignorecase={ignorecase}
                    searchByTitle={
                        searchByTitle
                            ? `${getLocalizedText(commonTexts.searchPrefix)} ${searchByTitle}`
                            : undefined
                    }
                    searchFromBeginning={searchFromBeginning}
                    handleIgnoreCaseChange={handleIgnoreCaseChange}
                    handleSearchFromBeginningChange={handleSearchFromBeginningChange}
                    handleSearchByChange={handleSearchByChange}
                    handleSearch={handleSearch}
                    isSearchAsync={isSearchable}
                />

                {isOpen && (
                    <div>
                        <Scrollbar
                            autoHeightMax={300}
                            autoHeight
                            rtl={isRTL()}
                            onScrollEnd={handleScroll}
                            shiftVerticalScrollPositionX={6}
                        >
                            <div className="pui-dropdown-options">
                                {options.length > 0 ? (
                                    options.map((itenData, index) => {
                                        const item = prepareDataItem(itenData);

                                        return (
                                            <DropdownItem
                                                key={index}
                                                itemBorder
                                                data={item}
                                                onSelect={onSelect}
                                                isFocused={focusedIndex === index}
                                                isSelected={isSelected(item, value, false)}
                                                contentRenderer={
                                                    isSearchable ? contentRenderer : undefined
                                                }
                                            />
                                        );
                                    })
                                ) : (
                                    <Text
                                        componentClasses={styles.noOptions}
                                        skin="paragraph-2"
                                        tag="div"
                                        textColor="secondary-400"
                                    >
                                        {texts.no_options_text}
                                    </Text>
                                )}
                            </div>
                        </Scrollbar>
                    </div>
                )}
            </DropdownV2>
        </DropDownV2Context.Provider>
    );
};
